"use client";

import React, { useState, useRef } from "react";
import { StandardModal, Button, LoadingSpinner } from "@wtc/ui";
import { useUploadGoogleDriveFile } from "@/lib/hooks/google-drive/use-google-drive-browser";
import { toast } from "sonner";
import { Upload, File, X, CheckCircle } from "lucide-react";

export interface GoogleDriveUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId?: string;
  driveId?: string;
  isSecondAccount?: boolean;
  onSuccess?: (file: any) => void;
  multiple?: boolean;
}

export function GoogleDriveUploadModal({
  open,
  onOpenChange,
  parentId,
  driveId,
  isSecondAccount = false,
  onSuccess,
  multiple = true,
}: GoogleDriveUploadModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadGoogleDriveFile();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => (multiple ? [...prev, ...files] : files));
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    console.log("Uploading files with context:", {
      parentId,
      driveId,
      isSecondAccount,
      fileCount: selectedFiles.length,
    });

    const results: any[] = [];

    for (const file of selectedFiles) {
      try {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        const result = await uploadMutation.mutateAsync({
          file,
          parentId,
          driveId,
          isSecondAccount,
        });

        results.push(result.file);
        setUploadedFiles((prev) => [...prev, file.name]);
        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));

        toast.success(`"${file.name}" uploaded successfully`);
      } catch (error: any) {
        console.error(`Error uploading ${file.name}:`, error);
        toast.error(`Failed to upload "${file.name}": ${error.message}`);
      }
    }

    if (results.length > 0) {
      onSuccess?.(results);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onOpenChange(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <StandardModal
      isOpen={open}
      onClose={handleClose}
      title="Upload Files"
      subtitle="Select files to upload to Google Drive"
      icon={Upload}
      maxWidth="lg"
    >
      <div className="space-y-4">
        {/* File Input */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            onChange={handleFileSelect}
            className="hidden"
            accept="*/*"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="w-full"
            disabled={uploadMutation.isPending}
          >
            <Upload className="h-4 w-4 mr-2" />
            {multiple ? "Select Files" : "Select File"}
          </Button>
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Selected Files:</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between p-2 border rounded-lg"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{file.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                    {uploadedFiles.includes(file.name) && (
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                  </div>

                  {!uploadedFiles.includes(file.name) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={uploadMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}

                  {/* Upload Progress */}
                  {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                    <div className="w-full mt-2">
                      <div className="w-full bg-card-200 rounded-full h-1">
                        <div
                          className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[file.name]}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={uploadMutation.isPending || selectedFiles.length === 0}
          >
            {uploadMutation.isPending ? (
              <>
                <LoadingSpinner className="h-4 w-4 mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      </div>
    </StandardModal>
  );
}
